import * as Config from './config';
import * as Strings from '../Locales/en.json';

/**
 * Gets the index of today's date, offset to match Google's date index
 * 
 * @returns {number} index of today's date
 */
export function getDateIndex() {
    var today = new Date();
    var todayIndex = today.getDay();
    return todayIndex = 0 ? 6 : todayIndex - 1;
}

/**
 * Title cases any given string
 * 
 * @param {string} originalString - String to be title cased
 * @returns {string} title cased string
 */
String.prototype.toTitleCase = function(originalString) {
    var s = this;
    if (1 !== originalString) s = s.toLowerCase();
    return s.replace(/(^|\s)[a-z]/g, (f) => {
        return f.toUpperCase()
    });
}

/**
 * Pads the left of any given number with 0's up to a given size
 * 
 * @param {number} int - number to be padded with 0's
 * @param {number} size - size for the returned string to be
 * @returns {string} left padded string
 */
export function leftPad(int, size) {
    // DINs are all 8 digit numbers preceeded by a left pad of zeros
    var num = int.toString();
    while (num.length < size) {
        num = "0" + num;
    }
    return num;
}

/**
 * Takes any given string and attempts to format as a phone number
 * 
 * @param {string} number - string representing a phone number
 */
export function formatPhoneNumber(number) {
    // Removes all non-digit characters from the string
    number = number.replace(/\D+/g, '');
    if (number.length < 7) {
        return "No Valid Number";
    }
    let lineNumber = number.slice(-4);
    let prefix = number.slice(-7, -4);
    let areaCode = '';
    if (number.length >= 10) {
        areaCode = `(${number.slice(-10, -7)})`;
    }
    let countryCode = number.slice(-13, -10).replace(/^0+/, '+');
    return `${countryCode} ${areaCode} ${prefix}-${lineNumber}`.trim();
}

/**
 * Returns the distance, in kilometers, of the shortest between the center and top edge or center and right edge of the map's visible area
 * 
 * @param {Map} map - A Google Maps Map object
 * @returns {number} The number of kilometers from the center to the closest edge of the provided Google Maps map
 */
export function getShortRadius(map) {
    var bounds = map.getBounds();
    var center = map.getCenter();
    var northEast = bounds.getNorthEast()
    var eastEdgeCenter = new google.maps.LatLng({ 
        lat: center.lat(),
        lng: northEast.lng()
    });
    var northEdgeCenter = new google.maps.LatLng({
        lat: northEast.lat(),
        lng: center.lng()
    });

    // Uses the google maps API to calculate distance between the center and the north and east edges
    var radii = [google.maps.geometry.spherical.computeDistanceBetween(center, eastEdgeCenter), google.maps.geometry.spherical.computeDistanceBetween(center, northEdgeCenter)];
    // Returns the smallest of the two distances
    return Math.min(...radii)/1000;
}

/**
* Takes a file path or URL, a height, width, origin, and anchor and returns a Google Map icon object to be used.
* .svg files MUST have a height and width defined, and no viewBox property.

* @param iconPath {string} Path to the icon file
* @param height {number} Height of the icon in pixels
* @param width {number} Width of the icon in pixels
* @param originX {number} X origin of the image (optional, default to 0)
* @param originY {number} Y origin of the image (optional, default to 0)
* @param anchorX {number} The X position on the image that will be placed on the actual location (optional)
* @param anchorY {number} The Y position on the image that will be placed on the actual location (optional)
*
* @returns {object} A properly formatted object for passing to the icon: property in a google map's marker
*/
export function generateGoogleMapIconForSvg(iconPath, height, width, originX = 0, originY = 0, anchorX, anchorY) {
    let iconObject = {
        url: iconPath,
        scaledSize: new google.maps.Size(height, width),
        origin: new google.maps.Point(originX, originY)
    }
    if (anchorX && anchorY) {
        iconObject.anchor = new google.maps.Point(anchorX, anchorY);
    }
    return iconObject;
}

/**
 * Takes a literal template and any given number of arguments and returns a string
 * 
 * @param {string literal} literal - An ES6 string literal
 * @param {string(s)} substitutions - any number of substitutions
 */

export function i18n(dictionaryKey, ...substitutions) {
    let string = Strings[dictionaryKey];

    // a string literal passes an array of strings in to the function so we loop through this and tag on the substitutions
    for (let i = 0; i < substitutions.length; i++) {
        let substitution = substitutions[i];
        let substitutionRegex = new RegExp(`\\\$${i + 1}`);
        string = string.replace(substitutionRegex, substitution);
    }
    return string;
}

/**
 * Generates the html for displaying a drug price
 * 
 * @param {array} classes - array of classes to add to the div
 * @param {number/string} price - The price of the drug
 * @param {string} drug - The name of the drug
 * @param {string} strength - The strength of the drug
 * @returns {string} A formatted drug price string
 */
export function drugPriceHtml(classes, price, drug, strength) {
    let classString = classes.join(' ');
    drug = drug.toTitleCase();
    if (price) {
        return `<div class="${classString}">${i18n('pillPrice', drug, strength, price)}</div>`;
    } else {
        return `<div class="${classString}">${i18n('noCurrentData', drug, strength)}</div>`;
    }
}

/**
 * Adds a tooltip to the specified element
 * 
 * @param {string} selector - A CSS selector
 * @param {string} placement - A string of either top/left/bottom/right
 * @param {string} title - A title for the tooltip
 * @param {string} content - A string (will not be escaped) that will be the content for the tooltip
 * @returns {object} a jquery object of the affected element after modification
 */
export function addTooltip(selector, placement = '', content = 'tooltip', options) {
    let $el = $(selector);
    let defaultOptions = {
        delay: { "show": 250, "hide": 250 },
        html: true,
        placement: `auto ${placement}`,
        title: content
    }
    options = _.merge(defaultOptions, options);
    $el.attr('data-toggle', 'tooltip');
    $el.tooltip(options);
    return $el;
}

/**
 * Returns the basic html for a button with id attached
 * 
 * @param {string} id - id to set to the button html
 * @returns {string} html string to add button
 */
export function generateButtonHtml(id, content, ...classes) {
    let classAttr = classes.join(' ') || '';
    return `<div id="${id}" class="pc-button ${classAttr}" tabindex="0">${content}</div>`;
}


// WEBPACK FOOTER //
// ../D:/builds/ACES/Pharmacy Compass Production Support/PharmacyCompass2/Content/scripts/utils.js